import { inject, Injectable } from '@angular/core';
import {
  AssignedPersonFormValues,
  AssignedPersonTariffFormValues
} from '@core/data-access/models/form/form-provider.model';
import { PersonType } from '@core/data-access/models/person.model';
import { FormProvider } from '@core/data-access/services/form/form-provider.service';
import { CreateSavedTicketHoldersRequest } from '@core/features/account/features/saved-ticket-holders/models/saved-ticket-holders-api.model';
import { mapFormValuesToCreateTicketHolderRequest } from '@core/features/account/features/saved-ticket-holders/utils/saved-ticket-holder-mappers';
import { Store } from '@ngrx/store';
import { TranslateService } from '@ngx-translate/core';
import { TariffUuid, TicketPersonId } from '@products/models/tariff.model';
import { selectAllTicketHolderTariffUuidsWithTicketPersonId } from '@store/products/booking/tariff/booking-tariff.selectors';
import { defaults, isNull, reduce, some } from 'lodash';
import { combineLatest, first, map } from 'rxjs';

@Injectable({
  providedIn: 'root'
})
export class CreateSavedTicketHolders {
  readonly #store = inject(Store);
  readonly #translateService = inject(TranslateService);
  readonly #formProvider = inject(FormProvider);

  prepareUnsavedPersonsForCreateSavedTicketHoldersRequest$() {
    return combineLatest({
      buyerInformationPersonAndFormValues: this.#formProvider.getBuyerInformationPersonAndFormValues$(
        this.#translateService
      ),
      assignedPersonTariffFormValues:
        this.#formProvider.getAssignedPersonTariffFormValuesFromBuyerFormGroupedByTariffUuidOrTicketHolderForms$(
          this.#translateService
        ),
      allTicketHolderTariffUuidsWithTicketPersonId: this.#store.select(
        selectAllTicketHolderTariffUuidsWithTicketPersonId
      )
    }).pipe(
      first(),
      map(
        ({
          assignedPersonTariffFormValues,
          buyerInformationPersonAndFormValues,
          allTicketHolderTariffUuidsWithTicketPersonId
        }) =>
          this.mapUnsavedAssignedPersonsFormValuesToCreateSavedTicketHoldersRequest(
            assignedPersonTariffFormValues,
            buyerInformationPersonAndFormValues,
            allTicketHolderTariffUuidsWithTicketPersonId
          )
      )
    );
  }

  private mapUnsavedAssignedPersonsFormValuesToCreateSavedTicketHoldersRequest(
    assignedPersonTariffFormValues: AssignedPersonTariffFormValues,
    buyerInformationPersonAndFormValues: AssignedPersonFormValues,
    allTicketHolderTariffUuidsWithTicketPersonId: Record<TariffUuid, TicketPersonId>
  ): CreateSavedTicketHoldersRequest {
    const { person: buyerPerson, formValues: buyerFormValues } = buyerInformationPersonAndFormValues;

    const createSavedTicketHoldersRequest = reduce(
      assignedPersonTariffFormValues,
      (createSavedTicketHolderRequest, unsavedAssignedPersonFormValue, tariffUuid) => {
        const { person: ticketHolderPerson, formValues: ticketHolderFormValues } = unsavedAssignedPersonFormValue;
        const { id, uuid, type } = ticketHolderPerson;

        if (!isNull(id)) {
          return createSavedTicketHolderRequest;
        }

        const isPersonTypeBuyer = type === PersonType.BuyerIsHolder;
        const personType = isPersonTypeBuyer ? PersonType.Buyer : type;
        const formValues = isPersonTypeBuyer
          ? defaults({}, ticketHolderFormValues, buyerFormValues)
          : ticketHolderFormValues;

        createSavedTicketHolderRequest.push(
          mapFormValuesToCreateTicketHolderRequest(
            personType,
            formValues,
            uuid,
            allTicketHolderTariffUuidsWithTicketPersonId[tariffUuid]
          )
        );

        return createSavedTicketHolderRequest;
      },
      [] as CreateSavedTicketHoldersRequest
    );

    const isBuyerPersonAlreadyCreated = !isNull(buyerPerson.id);
    const buyerTicketHolderExists = some(
      createSavedTicketHoldersRequest,
      ({ personType }) => personType === PersonType.Buyer
    );

    if (!isBuyerPersonAlreadyCreated && !buyerTicketHolderExists) {
      createSavedTicketHoldersRequest.unshift(
        mapFormValuesToCreateTicketHolderRequest(buyerPerson.type, buyerFormValues, buyerPerson.uuid)
      );
    }

    return createSavedTicketHoldersRequest;
  }
}
